import type {
  AvailableActions,
  AvailableStateCode,
  BeneficiaryData,
  ExternalBeneficiaryBankData,
  InternalBeneficiaryBankData,
} from '@/components/root/details/tabs/cashTransfers/cashTransfer.model';
import {
  type BaseCashTransferForm,
  mapCashTransferFormToRequestPayload,
} from '@/components/root/details/transfer/model/cashTransferFormModel';
import type { ExternalCashTransferDetails } from '@/components/root/details/transfer/model/external';
import type { InternalCashTransferDetails } from '@/components/root/details/transfer/model/internal';
import type { ResponsePayloadCashTransferDetails } from '@/components/root/details/transfer/model/responsePayload';
import { isDefined } from '@/utils/assertions';
import { type PayloadAction, createSlice, isAnyOf } from '@reduxjs/toolkit';
import type { Failure } from '../api/WebApiResponse';
import { type IBANObject, cashTransferApi } from '../api/cashTransfer.api';

export const availableModalState = [
  'none',
  'writable', // formulaire
  'editable', // readonly + editable field
  'validatable', // readonly + can validate but no modif allowed
  'readonly', // readonly
  'draftSuccess',
  'createdSuccess',
  'updateSuccess',
  'validateSuccess',
  'cancelSuccess',
] as const;
export type ModalState = (typeof availableModalState)[number];

type ModalStateUpdate = {
  modalState: ModalState;
  referenceCode?: string;
  action?: AvailableActions;
};

type CommonModalState = {
  modalState: ModalState;
  cashTransferCanBeCreate: boolean;
  statusCode?: AvailableStateCode;
  actualReferenceCode: string;
  action?: AvailableActions;
  rootId: string;
  cashTransferCommonDatas: BaseCashTransferForm;
  cashTransferDetails?: ResponsePayloadCashTransferDetails;
  failuresReason: Failure[];
  ibans: IBANObject[];
};

type CashTransferModalInitial = CommonModalState & {
  transferType: undefined;
};

export type CashTransferModalStateInternal = CommonModalState & {
  transferType: 'internal';
  details: InternalCashTransferDetails;
  beneficiaryBank: InternalBeneficiaryBankData;
  beneficiaryData: Partial<BeneficiaryData>;
};

export type CashTransferModalStateExternal = CommonModalState & {
  transferType: 'external';
  details: ExternalCashTransferDetails;
  beneficiaryBank: ExternalBeneficiaryBankData;
  beneficiaryData: BeneficiaryData;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
};

type PossibleState =
  | CashTransferModalInitial
  | CashTransferModalStateExternal
  | CashTransferModalStateInternal;

const initialState: PossibleState = {
  modalState: 'none',
  statusCode: undefined,
  action: undefined,
  cashTransferDetails: undefined,
  cashTransferCanBeCreate: false,
  failuresReason: [],
  rootId: '',
  actualReferenceCode: '',
  transferType: undefined,
  cashTransferCommonDatas: {
    communication: '',
    sourceIBAN: {},
  },
  signatureVisibnility: false,
  ibans: [],
} as PossibleState;

export const cashTransferModalSlice = createSlice({
  name: 'cashTransferModal',
  initialState,
  reducers: {
    setModalRootId: (state, { payload }: PayloadAction<string>) => {
      state.rootId = payload;
    },
    setCashTransferModalState: (
      state,
      {
        payload: { modalState: newModalState, referenceCode, action },
      }: PayloadAction<ModalStateUpdate>,
    ) => {
      state.modalState = newModalState;
      state.actualReferenceCode = referenceCode ?? state.actualReferenceCode;
      state.action = action;
    },
    closeCashTransferModal: (state) => {
      state.actualReferenceCode = '';
      state.statusCode = undefined;
      state.modalState = 'none';
      state.transferType = undefined;
      state.cashTransferCommonDatas = {
        communication: '',
        sourceIBAN: {},
      };
      state.cashTransferCanBeCreate = false;
      state.cashTransferDetails = undefined;
      state.action = undefined;
      state.failuresReason = [];
    },
    resetModalState: (state) => {
      state.actualReferenceCode = '';
      state.statusCode = undefined;
      state.transferType = undefined;
      state.cashTransferCommonDatas = {
        communication: '',
        sourceIBAN: {},
      };
      state.cashTransferCanBeCreate = false;
      state.cashTransferDetails = undefined;
      state.action = undefined;
      state.failuresReason = [];
    },
    setTransferType: (state, { payload }: PayloadAction<'internal' | 'external'>) => {
      const defaultCurrency = state.cashTransferCommonDatas.sourceIBAN?.currency ?? 'EUR';
      const defaultExecutionDate = new Date().toISOString();
      // internal
      if (payload === 'internal') {
        return {
          ...state,
          transferType: 'internal',
          details: {
            currency: defaultCurrency,
            executionDate: defaultExecutionDate,
            amount: undefined,
          },
          beneficiaryBank: { name: '', ibanCode: '', root: '' },
          beneficiaryData: { name: '' },
        };
      }

      // external
      return {
        ...state,
        transferType: 'external',
        details: {
          currency: defaultCurrency,
          feesType: 'shared',
          paymentMode: 'normal',
          executionDate: defaultExecutionDate,
          amount: undefined,
        },
        beneficiaryBank: {
          swiftAddress: '',
          targetAccountType: 'iban',
          addressData: {
            countryCode: '',
          },
        },
        beneficiaryData: {
          addressData: {},
          name: '',
        },
      };
    },
    updateCashTransferFormCommonData: (
      state,
      { payload }: PayloadAction<Partial<BaseCashTransferForm>>,
    ) => {
      state.cashTransferCommonDatas = {
        ...state.cashTransferCommonDatas,
        ...payload,
        sourceIBAN: {
          ...state.cashTransferCommonDatas.sourceIBAN,
          ...payload.sourceIBAN,
        },
      };

      if (state.transferType === 'internal' && isDefined(payload.sourceIBAN)) {
        // When changing sourceIban, need to update currency in details
        state.details.currency = payload.sourceIBAN.currency ?? 'EUR';
      }
    },
    setDetailsInternal: (
      state,
      { payload }: PayloadAction<Partial<InternalCashTransferDetails>>,
    ) => {
      if (state.transferType === 'internal') {
        state.details = {
          ...state.details,
          ...payload,
        };
      }
    },
    setIbans: (state, { payload }: PayloadAction<IBANObject[]>) => {
      state.ibans = payload;
    },
    setDetailsExternal: (
      state,
      { payload }: PayloadAction<Partial<ExternalCashTransferDetails>>,
    ) => {
      if (state.transferType === 'external') {
        state.details = {
          ...state.details,
          ...payload,
        };
      }
    },
    setBeneficiaryBankDataInternal: (
      state,
      { payload }: PayloadAction<Partial<InternalBeneficiaryBankData>>,
    ) => {
      if (state.transferType === 'internal') {
        state.beneficiaryBank = {
          ...state.beneficiaryBank,
          ...payload,
        };
      }
    },
    setBeneficiaryBankDataExternal: (
      state,
      { payload }: PayloadAction<Partial<ExternalBeneficiaryBankData>>,
    ) => {
      if (state.transferType === 'external') {
        state.beneficiaryBank = {
          ...state.beneficiaryBank,
          ...payload,
          addressData: {
            countryCode:
              payload.addressData?.countryCode ?? state.beneficiaryBank.addressData.countryCode,
          },
        };
      }
    },
    setBeneficiaryDataExternal: (state, { payload }: PayloadAction<Partial<BeneficiaryData>>) => {
      if (state.transferType === 'external') {
        state.beneficiaryData = {
          ...state.beneficiaryData,
          ...payload,
          addressData: {
            ...state.beneficiaryData?.addressData,
            ...payload.addressData,
          },
        };
      }
    },
  },
  selectors: {
    selectIbans: (state) => state.ibans,
    selectCashTransferModalState: (state) => state.modalState,
    selectCashTransferModalAction: (state) => state.action,
    selectActualCashTransferReferenceCode: (state) => state.actualReferenceCode,
    selectOpenedCashTransferAction: (state) => state.action,
    selectCashTransferForm: (state) => state.cashTransferCommonDatas,
    selectCashTransferFormTransferType: (state) => state.transferType,
    selectCashTransferIban: (state) => state.cashTransferCommonDatas.sourceIBAN.iban,
    selectCashTransferCurrencySource: (state) => state.cashTransferCommonDatas.sourceIBAN?.currency,
    selectCashTransferCommunication: (state) => state.cashTransferCommonDatas.communication,
    selectCashTransferDetails: (state) =>
      state.transferType !== undefined ? state.details : undefined,
    selectModalRootId: (state) => state.rootId,
    selectExternalCountryCode: (state) =>
      state.transferType === 'external' ? state.beneficiaryBank?.addressData?.countryCode : '',
    selectExternalBeneficiaryBank: (state) =>
      state.transferType === 'external' ? state.beneficiaryBank : undefined,
    selectExternalBeneficiaryData: (state) =>
      state.transferType === 'external' ? state.beneficiaryData : undefined,
    selectExternalDetailsData: (state) =>
      state.transferType === 'external' ? state.details : undefined,
    selectInternalBeneficiaryData: (state) =>
      state.transferType === 'internal' ? state.beneficiaryBank : undefined,
    selectCashTransferFormState: (state) => {
      if (state.transferType === 'external' || state.transferType === 'internal') {
        return mapCashTransferFormToRequestPayload(state);
      }
    },
    selectFailuresReason: (state) => state.failuresReason,
    selectCashTransferDetailsResponse: (state) => state.cashTransferDetails,
    selectCanCreateCashTransfer: (state) => state.cashTransferCanBeCreate,
    selectErrorOnFields: (state, field: string) =>
      state.failuresReason.filter(({ fields }) => fields.includes(field))?.[0],
    selectStatusCode: (state) => state.statusCode,
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      isAnyOf(cashTransferApi.endpoints.createCashTransfer.matchFulfilled),
      (state, action) => {
        const isDraftRequest = action?.meta.arg.originalArgs.payload.draftMode;
        const referenceCode = action?.payload?.data[0];
        state.actualReferenceCode = referenceCode;
        state.modalState = isDraftRequest ? 'draftSuccess' : 'createdSuccess';
      },
    );
    builder.addMatcher(
      isAnyOf(cashTransferApi.endpoints.updateCashTransfer.matchFulfilled),
      (state, action) => {
        const referenceCode = action?.payload?.data[0];
        const previousStatusCode = state.statusCode;
        state.actualReferenceCode = referenceCode;
        state.modalState = previousStatusCode === 'draft' ? 'createdSuccess' : 'updateSuccess';
      },
    );
    builder.addMatcher(
      isAnyOf(cashTransferApi.endpoints.validateCashTransfer.matchFulfilled),
      (state, action) => {
        const result = action?.payload?.data[0];
        state.actualReferenceCode = result.transferId;
        state.statusCode = result.status;
        state.modalState = 'validateSuccess';
        state.action = undefined;
      },
    );
    builder.addMatcher(
      isAnyOf(cashTransferApi.endpoints.cancelCashTransfer.matchFulfilled),
      (state, action) => {
        const referenceCode = action?.payload?.data[0];
        state.actualReferenceCode = referenceCode;
        state.modalState = 'cancelSuccess';
        state.action = undefined;
      },
    );
    builder.addMatcher(
      isAnyOf(
        cashTransferApi.endpoints.checkCanCreateCashTransfer.matchFulfilled,
        cashTransferApi.endpoints.checkCanUpdateCashTransfer.matchFulfilled,
      ),
      (state, { payload }) => {
        if (payload.data[0].debitData?.cashTransferTypeCode === 'internal') {
          const { detailsData, beneficiaryBankData, communication, debitData, beneficiaryData } =
            payload.data[0];
          return {
            ...state,
            modalState: 'editable',
            failuresReason: payload.failures,
            cashTransferCanBeCreate: true,
            cashTransferDetails: payload.data[0],
            transferType: 'internal',
            details: detailsData,
            cashTransferCommonDatas: {
              communication,
              sourceIBAN: {
                accountId: debitData?.ibanAccountId,
                iban: debitData?.ibanCode,
                currency: detailsData?.currency,
                generalAccountNumber: debitData.generalAccountNumber,
              },
            },
            beneficiaryBank: beneficiaryBankData,
            beneficiaryData: { name: '' },
          };
        }

        // External state
        const { detailsData, beneficiaryBankData, communication, debitData, beneficiaryData } = payload.data[0];

        return {
          ...state,
          modalState: 'editable',
          failuresReason: payload.failures,
          cashTransferCanBeCreate: true,
          cashTransferDetails: payload.data[0],
          transferType: 'external',
          details: {
            ...detailsData,
            feesType: detailsData?.feeTypeCode,
            paymentMode: detailsData?.paymentMethod,
          },
          cashTransferCommonDatas: {
            communication,
            sourceIBAN: {
              accountId: debitData?.ibanAccountId,
              iban: debitData?.ibanCode,
              currency: debitData?.ibanCurrency,
              generalAccountNumber: debitData.generalAccountNumber,
            },
          },
          beneficiaryBank: beneficiaryBankData,
          beneficiaryData,
        };
      },
    );
    builder.addMatcher(
      isAnyOf(
        cashTransferApi.endpoints.checkCanCreateCashTransfer.matchRejected,
        cashTransferApi.endpoints.checkCanUpdateCashTransfer.matchRejected,
        cashTransferApi.endpoints.validateCashTransfer.matchRejected,
      ),
      (state, { payload }) => {
        if (isDefined(payload) && payload.type === 'functionalFailures') {
          return {
            ...state,
            failuresReason: payload.failures,
          };
        }
      },
    );
    builder.addMatcher(
      isAnyOf(cashTransferApi.endpoints.getCashTransferById.matchFulfilled),
      (
        state,
        {
          payload,
          meta: {
            arg: { 
              originalArgs: {
                referenceCode, entity
              }
             },
          },
        },
      ) => {
        if (payload?.debitData?.cashTransferTypeCode === 'internal') {
          const {
            detailsData,
            beneficiaryBankData,
            communication,
            debitData,
            statusCode,
            beneficiaryData,
          } = payload;
          return {
            ...state,
            actualReferenceCode: referenceCode,
            statusCode: statusCode,
            cashTransferCanBeCreate: true,
            cashTransferDetails: payload,
            transferType: 'internal',
            details: {
              ...detailsData,
              ...(state.action === 'canCopy' && { executionDate: new Date().toISOString().split('T')[0] }),
            },
            cashTransferCommonDatas: {
              communication,
              sourceIBAN: {
                accountId: debitData?.ibanAccountId,
                iban: debitData?.ibanCode,
                currency: detailsData?.currency,
                generalAccountNumber: state.cashTransferCommonDatas.sourceIBAN.generalAccountNumber,
              },
            },
            beneficiaryBank: beneficiaryBankData,
            beneficiaryData: { name: '' },
          };
        }

        // External state
        const {
          detailsData,
          beneficiaryBankData,
          communication,
          debitData,
          beneficiaryData,
          statusCode,
        } = payload;

        return {
          ...state,
          actualReferenceCode: referenceCode,
          cashTransferCanBeCreate: true,
          statusCode: statusCode,
          cashTransferDetails: payload,
          transferType: 'external',
          details: {
            ...detailsData,
            feesType: detailsData?.feeTypeCode,
            paymentMode: detailsData?.paymentMethod,
            ...(state.action === 'canCopy' && { executionDate: new Date().toISOString().split('T')[0] }),
            ...(state.action === 'canCopy' && (detailsData?.paymentMethod as string) === 'instant' && { paymentMode: 'normal' }),
          },
          cashTransferCommonDatas: {
            communication,
            sourceIBAN: {
              accountId: debitData?.ibanAccountId,
              iban: debitData?.ibanCode,
              currency: detailsData?.currency,
              generalAccountNumber: state.cashTransferCommonDatas.sourceIBAN.generalAccountNumber,
            },
          },
          beneficiaryBank: beneficiaryBankData,
          beneficiaryData,
        };
      },
    );
  },
});

export const {
  selectIbans,
  selectCashTransferModalState,
  selectCashTransferModalAction,
  selectActualCashTransferReferenceCode,
  selectCashTransferForm,
  selectCashTransferFormTransferType,
  selectCashTransferCurrencySource,
  selectCashTransferCommunication,
  selectCashTransferDetails,
  selectModalRootId,
  selectExternalCountryCode,
  selectExternalBeneficiaryBank,
  selectExternalBeneficiaryData,
  selectExternalDetailsData,
  selectInternalBeneficiaryData,
  selectCashTransferFormState,
  selectCanCreateCashTransfer,
  selectCashTransferDetailsResponse,
  selectOpenedCashTransferAction,
  selectFailuresReason,
  selectErrorOnFields,
  selectStatusCode,
  selectCashTransferIban,
} = cashTransferModalSlice.selectors;

export const {
  setIbans,
  setCashTransferModalState,
  closeCashTransferModal,
  resetModalState,
  updateCashTransferFormCommonData,
  setTransferType,
  setDetailsExternal,
  setDetailsInternal,
  setBeneficiaryBankDataInternal,
  setModalRootId,
  setBeneficiaryBankDataExternal,
  setBeneficiaryDataExternal,
} = cashTransferModalSlice.actions;
